import React from 'react';
import { MousePointer2, Radio, Users, Hand, LogOut, GripVertical } from 'lucide-react';

const COLORS = ['bg-indigo-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500'];
const colorFor = (id) => COLORS[(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
const textFor = (id) => (colorFor(id).replace('bg-', 'text-'));
const initials = (name) => (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

// Remote collaborators' live cursors, positioned in per-mille of the canvas.
export function CoEditCursors({ cursors }) {
  return (
    <>
      {Object.entries(cursors).map(([uid, c]) => (
        <div key={uid} className="pointer-events-none absolute z-50 transition-all duration-75"
          style={{ left: `${(c.x || 0) / 10}%`, top: `${(c.y || 0) / 10}%` }} data-testid={`coedit-cursor-${uid}`}>
          <MousePointer2 className={`w-4 h-4 ${textFor(uid)} fill-current drop-shadow`} />
          <span className={`ml-3 -mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${colorFor(uid)} text-white text-[9px] font-semibold whitespace-nowrap`}>
            {c.avatarUrl && <img src={c.avatarUrl} alt="" className="w-3.5 h-3.5 rounded-full object-cover ring-1 ring-white/60" />}
            {c.name || 'Guest'}
          </span>
        </div>
      ))}
    </>
  );
}

// Header bar: who's in the room, who holds edit control, and the request/take
// control affordances.
export function CoEditBar({ members, editor, amEditor, me, onRequest, onTake, onLeave, dragHandle, onColor }) {
  if (!members || members.length <= 1) return null;
  const editorName = members.find(m => m.user_id === editor)?.name || 'Someone';
  const muted = onColor ? 'text-white/85' : 'text-muted-foreground';
  return (
    <div className="flex items-center gap-3" data-testid="coedit-bar">
      {dragHandle && (
        <span onMouseDown={dragHandle} className={`cursor-grab active:cursor-grabbing -ml-1 ${onColor ? 'text-white/70 hover:text-white' : 'text-muted-foreground/80 hover:text-muted-foreground'}`} data-testid="coedit-drag-handle" title="Drag to move">
          <GripVertical className="w-4 h-4" />
        </span>
      )}
      <span className={`flex items-center gap-1 text-[11px] ${muted}`}><Users className="w-3 h-3" /> {members.length} editing</span>
      <div className="flex items-center -space-x-2">
        {members.slice(0, 6).map(m => (
          <div key={m.user_id}
            title={m.user_id === editor ? `${m.name} — has edit control` : m.name}
            data-testid={`coedit-member-${m.user_id}`}
            className={`relative w-7 h-7 rounded-full overflow-hidden flex items-center justify-center ring-2 ${m.user_id === editor ? 'ring-emerald-300' : (onColor ? 'ring-white/50' : 'ring-background')} ${m.avatarUrl ? '' : `${colorFor(m.user_id)} text-white text-[10px] font-semibold`}`}>
            {m.avatarUrl ? <img src={m.avatarUrl} alt={m.name || ''} className="w-full h-full object-cover" /> : initials(m.name)}
          </div>
        ))}
      </div>
      {amEditor ? (
        <span className={`flex items-center gap-1 text-[10px] font-semibold ${onColor ? 'text-emerald-200' : 'text-emerald-400'}`} data-testid="coedit-you-have-control"><Radio className="w-3 h-3 animate-pulse" /> You have edit control</span>
      ) : (
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] ${onColor ? 'text-amber-200' : 'text-amber-400'}`}>{editorName} is editing</span>
          <button onClick={onRequest} className={`text-[10px] px-2 py-0.5 rounded-md border ${onColor ? 'border-white/40 text-white/90 hover:bg-white/10' : 'border-border/50 text-muted-foreground hover:text-foreground'}`} data-testid="coedit-request-control"><Hand className="w-3 h-3 inline mr-0.5" />Request</button>
          <button onClick={onTake} className={`text-[10px] px-2 py-0.5 rounded-md border ${onColor ? 'border-amber-200/70 text-amber-100 hover:bg-white/10' : 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10'}`} data-testid="coedit-take-control">Take control</button>
        </div>
      )}
      {onLeave && (
        <button onClick={onLeave} className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md border ${onColor ? 'border-white/50 text-white hover:bg-white/15' : 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10'}`} data-testid="coedit-leave" title="Leave the co-editing session">
          <LogOut className="w-3 h-3" /> Leave
        </button>
      )}
    </div>
  );
}
